import React from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { cn } from "@/shared/lib/utils/cn";

interface PermissionTooltipProps {
  /** Whether the user has permission to use the wrapped control. */
  allowed: boolean;
  /** Message shown in the tooltip when `allowed` is false. */
  message: string;
  /**
   * The control to render (e.g. a Button or DropdownMenuItem).
   * When `allowed` is false, this element is cloned with `disabled: true` -
   * make sure it accepts a `disabled` prop in that case.
   */
  children: React.ReactElement;
  /**
   * Optional className applied to the inner wrapper span.
   * Defaults to "w-fit". Pass "w-full" when wrapping a full-width control
   * such as StatusSwitcher or AssigneeSwitcher.
   */
  className?: string;
  /** Side of the trigger the tooltip should render on. Defaults to "top". */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment of the tooltip relative to the trigger. Defaults to "center". */
  align?: "start" | "center" | "end";
}

/**
 * Wraps an action control (button, menu item, etc.) so that when the user
 * doesn't have permission to use it, the control is still visible but
 * rendered disabled with a tooltip explaining why.
 *
 * When `allowed` is true, the child is rendered unchanged.
 */
export function PermissionTooltip({ allowed, message, children, className, side = "top", align = "center" }: PermissionTooltipProps) {
  if (allowed) {
    return children;
  }

  return (
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex w-fit", className)}>
          {React.cloneElement(children, {
            disabled: true,
          } as React.Attributes & { disabled?: boolean })}
        </span>
      </TooltipTrigger>
      <TooltipContent side={side} align={align}>
        {message}
      </TooltipContent>
    </Tooltip>
  );
}
